import React, { useState } from 'react';
import { Wrench, AlertTriangle, CheckCircle, Play, Eye, RefreshCw, Upload, Package, FileJson } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Migration Tool Component
const MigrationTool = ({ 
  title, 
  description, 
  validOptions, 
  previewEndpoint, 
  executeEndpoint,
  fieldName = 'mood'
}) => {
  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);

  const handlePreview = async () => {
    setLoading(true);
    setPreviewData(null);
    setMigrationResult(null);
    
    try {
      const token = sessionStorage.getItem('_at');
      const response = await fetch(`${API_URL}${previewEndpoint}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      if (response.ok) {
        const data = await response.json();
        setPreviewData(data);
        const updateCount = data.products_updated || data.products_to_update || 0;
        if (updateCount === 0) {
          toast.success(`No hay ${fieldName}s inválidos para corregir`);
        } else {
          toast.info(`${updateCount} productos necesitan actualización`);
        }
      } else {
        toast.error('Error al obtener vista previa');
      }
    } catch (error) {
      toast.error('Error de conexión');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleMigrate = async () => {
    const updateCount = previewData?.products_updated || previewData?.products_to_update || 0;
    if (!previewData || updateCount === 0) {
      toast.error('Primero ejecuta la vista previa');
      return;
    }

    const confirmed = window.confirm(
      `¿Estás seguro de actualizar ${updateCount} productos?\n\nEsta acción convertirá los valores inválidos a valores válidos del sistema.`
    );

    if (!confirmed) return;

    setLoading(true);
    
    try {
      const token = sessionStorage.getItem('_at');
      const response = await fetch(`${API_URL}${executeEndpoint}`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      if (response.ok) {
        const data = await response.json();
        setMigrationResult(data);
        setPreviewData(null);
        toast.success(`✅ ${data.products_updated} productos actualizados correctamente`);
      } else {
        toast.error('Error al ejecutar migración');
      }
    } catch (error) {
      toast.error('Error de conexión');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const updateCount = previewData?.products_updated || previewData?.products_to_update || 0;

  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden mb-6">
      <div className="p-6 border-b border-gray-700">
        <h2 className="text-lg font-semibold text-white mb-2">{title}</h2>
        <p className="text-gray-400 text-sm">{description}</p>
        
        <div className="mt-4 p-4 bg-gray-900 rounded-lg">
          <p className="text-xs text-gray-500 mb-2">Valores válidos del sistema:</p>
          <div className="flex flex-wrap gap-2">
            {validOptions.map(option => (
              <span key={option} className="px-3 py-1 bg-amber-500/20 text-amber-400 rounded-full text-xs">
                {option}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Action Buttons */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={handlePreview}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white rounded-lg transition-colors"
          >
            {loading ? <RefreshCw size={18} className="animate-spin" /> : <Eye size={18} />}
            Vista Previa
          </button>
          
          <button
            onClick={handleMigrate}
            disabled={loading || !previewData || updateCount === 0}
            className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
          >
            {loading ? <RefreshCw size={18} className="animate-spin" /> : <Play size={18} />}
            Ejecutar Corrección
          </button>
        </div>

        {/* Preview Result */}
        {previewData && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 bg-gray-900 rounded-lg text-center">
                <p className="text-2xl font-bold text-white">{previewData.total_products}</p>
                <p className="text-xs text-gray-500">Total productos</p>
              </div>
              <div className="p-4 bg-gray-900 rounded-lg text-center">
                <p className="text-2xl font-bold text-white">
                  {previewData.products_with_mood || previewData.products_with_intensity || 0}
                </p>
                <p className="text-xs text-gray-500">Con valor asignado</p>
              </div>
              <div className="p-4 bg-gray-900 rounded-lg text-center">
                <p className={`text-2xl font-bold ${updateCount > 0 ? 'text-amber-400' : 'text-green-400'}`}>
                  {updateCount}
                </p>
                <p className="text-xs text-gray-500">Necesitan corrección</p>
              </div>
            </div>

            {updateCount > 0 && previewData.changes && (
              <div className="mt-4">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle size={16} className="text-amber-400" />
                  <p className="text-sm text-amber-400">Cambios que se aplicarán:</p>
                </div>
                <div className="max-h-64 overflow-y-auto space-y-2">
                  {previewData.changes.map((change, idx) => (
                    <div key={idx} className="p-3 bg-gray-900 rounded-lg text-sm">
                      <p className="font-medium text-white">{change.product_code} - {change.product_name}</p>
                      <div className="flex items-center gap-2 mt-1 text-xs">
                        <span className="text-red-400 line-through">
                          {change.old_moods?.join(', ') || change.old_intensity}
                        </span>
                        <span className="text-gray-500">→</span>
                        <span className="text-green-400">
                          {change.new_moods?.join(', ') || change.new_intensity || '(vacío)'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {updateCount === 0 && (
              <div className="flex items-center gap-3 p-4 bg-green-900/30 border border-green-700 rounded-lg">
                <CheckCircle className="text-green-400" size={24} />
                <div>
                  <p className="text-green-400 font-medium">¡Todo está correcto!</p>
                  <p className="text-green-400/70 text-sm">No hay valores inválidos que corregir.</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Migration Result */}
        {migrationResult && (
          <div className="flex items-center gap-3 p-4 bg-green-900/30 border border-green-700 rounded-lg">
            <CheckCircle className="text-green-400" size={24} />
            <div>
              <p className="text-green-400 font-medium">Migración completada</p>
              <p className="text-green-400/70 text-sm">
                {migrationResult.products_updated} productos fueron actualizados correctamente.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const AdminTools = () => {
  const [orderJson, setOrderJson] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [previewOrder, setPreviewOrder] = useState(null);

  const handlePreviewOrder = () => {
    setImportResult(null);
    setPreviewOrder(null);
    
    if (!orderJson.trim()) {
      toast.error('Pega el JSON del pedido');
      return;
    }

    try {
      const parsed = JSON.parse(orderJson);
      
      // Validar campos requeridos
      const required = ['order_number', 'items', 'total', 'shipping_address'];
      const missing = required.filter(f => !parsed[f]);
      
      if (missing.length > 0) {
        toast.error(`Faltan campos requeridos: ${missing.join(', ')}`);
        return;
      }

      setPreviewOrder(parsed);
      toast.success('JSON válido - Revisa la vista previa');
    } catch (e) {
      toast.error('JSON inválido: ' + e.message);
    }
  };

  const handleImportOrder = async () => {
    if (!previewOrder) {
      toast.error('Primero valida el JSON');
      return;
    }

    const confirmed = window.confirm(
      `¿Importar pedido ${previewOrder.order_number}?\n\nTotal: $${previewOrder.total?.toLocaleString()}\nCliente: ${previewOrder.shipping_address?.recipient_name}\n\n⚠️ NO se cobrará, NO se enviarán correos, NO se modificará inventario.`
    );

    if (!confirmed) return;

    setImportLoading(true);
    try {
      const token = sessionStorage.getItem('_at');
      const response = await fetch(`${API_URL}/api/admin/orders/import-historical`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify(previewOrder)
      });

      const data = await response.json();

      if (response.ok) {
        setImportResult({ success: true, message: data.message, order_number: data.order_number });
        setPreviewOrder(null);
        setOrderJson('');
        toast.success(`✅ Pedido ${data.order_number} importado correctamente`);
      } else {
        setImportResult({ success: false, message: data.detail || 'Error al importar' });
        toast.error(data.detail || 'Error al importar pedido');
      }
    } catch (error) {
      toast.error('Error de conexión');
      console.error(error);
    } finally {
      setImportLoading(false);
    }
  };

  const clearImportForm = () => {
    setOrderJson('');
    setPreviewOrder(null);
    setImportResult(null);
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-3 mb-8">
        <Wrench className="text-amber-500" size={28} />
        <div>
          <h1 className="text-2xl font-bold text-white">Herramientas de Mantenimiento</h1>
          <p className="text-gray-400 text-sm">Utilidades para corregir datos del catálogo</p>
        </div>
      </div>

      {/* Import Historical Orders Tool */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden mb-6">
        <div className="p-6 border-b border-gray-700">
          <div className="flex items-center gap-3 mb-2">
            <Package className="text-green-400" size={24} />
            <h2 className="text-lg font-semibold text-white">Importar Pedido Histórico</h2>
          </div>
          <p className="text-gray-400 text-sm">
            Importa pedidos ya cobrados de otra base de datos. NO ejecuta cobros, webhooks, ni modifica inventario.
          </p>
        </div>

        <div className="p-6">
          {/* JSON Input */}
          <div className="mb-4">
            <label className="block text-sm text-gray-300 mb-2">
              <FileJson size={16} className="inline mr-2" />
              Pega el JSON del pedido:
            </label>
            <textarea
              value={orderJson}
              onChange={(e) => setOrderJson(e.target.value)}
              placeholder='{"order_number": "ALP-...", "items": [...], "total": 899, ...}'
              className="w-full h-48 bg-gray-900 border border-gray-600 rounded-lg p-4 text-white font-mono text-xs resize-none focus:border-amber-500 focus:outline-none"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 mb-6">
            <button
              onClick={handlePreviewOrder}
              disabled={importLoading || !orderJson.trim()}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
            >
              <Eye size={18} />
              Validar JSON
            </button>
            
            <button
              onClick={handleImportOrder}
              disabled={importLoading || !previewOrder}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
            >
              {importLoading ? <RefreshCw size={18} className="animate-spin" /> : <Upload size={18} />}
              Importar Pedido
            </button>

            {(previewOrder || importResult) && (
              <button
                onClick={clearImportForm}
                className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
              >
                Limpiar
              </button>
            )}
          </div>

          {/* Preview */}
          {previewOrder && (
            <div className="p-4 bg-gray-900 rounded-lg border border-amber-500/30 mb-4">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle size={16} className="text-amber-400" />
                <p className="text-sm text-amber-400 font-medium">Vista previa del pedido a importar:</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Número de pedido:</p>
                  <p className="text-white font-mono">{previewOrder.order_number}</p>
                </div>
                <div>
                  <p className="text-gray-500">Total:</p>
                  <p className="text-green-400 font-bold">${previewOrder.total?.toLocaleString()} MXN</p>
                </div>
                <div>
                  <p className="text-gray-500">Cliente:</p>
                  <p className="text-white">{previewOrder.shipping_address?.recipient_name}</p>
                </div>
                <div>
                  <p className="text-gray-500">Productos:</p>
                  <p className="text-white">{previewOrder.items?.length} items</p>
                </div>
                <div>
                  <p className="text-gray-500">Estado:</p>
                  <p className="text-white">{previewOrder.status || 'confirmed'} / {previewOrder.payment_status || 'paid'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Método de pago:</p>
                  <p className="text-white">{previewOrder.payment_method || 'card'}</p>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-700">
                <p className="text-gray-500 text-xs mb-2">Productos:</p>
                <div className="space-y-1">
                  {previewOrder.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-xs">
                      <span className="text-gray-300">{item.quantity}x {item.product_name}</span>
                      <span className="text-gray-400">${item.subtotal?.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Result */}
          {importResult && (
            <div className={`flex items-center gap-3 p-4 rounded-lg ${importResult.success ? 'bg-green-900/30 border border-green-700' : 'bg-red-900/30 border border-red-700'}`}>
              {importResult.success ? (
                <CheckCircle className="text-green-400" size={24} />
              ) : (
                <AlertTriangle className="text-red-400" size={24} />
              )}
              <div>
                <p className={importResult.success ? 'text-green-400 font-medium' : 'text-red-400 font-medium'}>
                  {importResult.success ? 'Pedido importado correctamente' : 'Error al importar'}
                </p>
                <p className={importResult.success ? 'text-green-400/70 text-sm' : 'text-red-400/70 text-sm'}>
                  {importResult.message}
                </p>
              </div>
            </div>
          )}

          {/* Warning Note */}
          <div className="mt-4 p-3 bg-amber-900/20 border border-amber-700/30 rounded-lg">
            <p className="text-xs text-amber-400/80">
              <strong>⚠️ Importante:</strong> Esta herramienta solo debe usarse para importar pedidos ya cobrados de otra base de datos.
              No ejecuta cobros en Stripe, no envía correos, no dispara webhooks, y no modifica el inventario.
            </p>
          </div>
        </div>
      </div>

      {/* Mood Migration Tool */}
      <MigrationTool
        title="Corrección de Moods/Ocasiones"
        description='Esta herramienta corrige los moods inválidos en los productos. Los moods antiguos como "Fresco y limpio", "Seductor", "Formal" se convertirán a los moods válidos del sistema.'
        validOptions={['Perfecto para la noche', 'Para el día a día', 'Elegante', 'Sexy']}
        previewEndpoint="/api/admin/tools/migrate-moods/preview"
        executeEndpoint="/api/admin/tools/migrate-moods/execute"
        fieldName="mood"
      />

      {/* Intensity Migration Tool */}
      <MigrationTool
        title="Corrección de Intensidades"
        description='Esta herramienta corrige las intensidades inválidas en los productos. Los valores antiguos como "Ligera", "Moderada", "Fuerte" se convertirán a los valores válidos del sistema.'
        validOptions={['Sutil', 'Intenso', 'Monstruosa']}
        previewEndpoint="/api/admin/tools/migrate-intensities/preview"
        executeEndpoint="/api/admin/tools/migrate-intensities/execute"
        fieldName="intensidad"
      />

      {/* Olfactory Family Migration Tool */}
      <MigrationTool
        title="Corrección de Familias Olfativas"
        description='Esta herramienta corrige las familias olfativas inválidas en los productos. Las familias antiguas como "Amaderado", "Frutal", "Gourmand", "Oriental" se convertirán a las familias válidas del sistema o se eliminarán si no tienen equivalente.'
        validOptions={['Cítrico', 'Fresco', 'Dulce', 'Madera', 'Floral']}
        previewEndpoint="/api/admin/tools/migrate-olfactory-families/preview"
        executeEndpoint="/api/admin/tools/migrate-olfactory-families/execute"
        fieldName="familia olfativa"
      />

      {/* Info Note */}
      <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
        <p className="text-xs text-gray-500">
          <strong>Nota:</strong> Estas herramientas solo modifican los campos especificados de los productos. 
          Los valores que ya son válidos se mantienen sin cambios. Los valores inválidos se convierten 
          según el mapeo predefinido o se asigna un valor por defecto si no tienen equivalente.
        </p>
      </div>
    </div>
  );
};

export default AdminTools;
